import { useCallback, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';
import { Crown, Mars, Venus, Video } from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SIDEBAR_WIDTH_ICON, SIDEBAR_WIDTH, useSidebar } from '@/components/ui/sidebar';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

import { useUserInfo } from '../hooks/useUserInfo';
import { useGemBalance } from '../hooks/useGemBalance';
import { useVideo } from '../hooks/useVideo';
import { PAYMENT_EVENTS, UI_EVENTS, useTracking } from '@/hooks/useTracking';
import { supabase, SUPABASE_URL, supabaseEh } from '../lib/supabase';
import { EhUpgradeDialog } from '../components/EhUpgradeDialog';
import EhAuthDialog from '@/components/EhAuthDialog';
import { getMediaUrl, shuffleArray } from '@/lib/utils';
import { generateBase58Id } from '@/lib/id';
import { SEO } from '@/components/SEO';
import { EhGemsDialog } from '@/components/EhGemsDialog';
import { isFromAppStores, isHidingMonetizationInStores } from '@/lib/detection';
import { EhFlagDialog } from '@/components/EhFlagDialog';
import { Flag } from 'lucide-react';
import { COST_PER_IMAGE } from '@/lib/constants';

const FREE_IMAGE_DAILY_LIMIT = 2;
const FREE_VIDEO_MONTHLY_LIMIT = 1;

// Reuse options from CreateCharacterPage but organize differently
const STYLE_OPTIONS = [
  { value: 'realistic', label: 'Realistic' },
  { value: 'anime', label: 'Anime' },
];

const GENDER_OPTIONS = [
  { value: 'f', label: '♀️ Female' },
  { value: 'm', label: '♂️ Male' },
];

const SPICINESS_OPTIONS = [
  { value: 'casual', label: 'Casual' },
  { value: 'nude', label: '🍑 Intimate', storeLabel: '🍑 Intimate', locked: true },
  { value: 'explicit', label: '🌶️ Explicit', locked: true },
];  

const ETHNICITY_OPTIONS = [
  { value: 'caucasian', label: 'Caucasian', imgPath: 'f/eh-cr-f-ethn-caucasian-2.webp', mImgPath: 'm/eh-cr-m-ethn-caucasian.webp' },
  {
    value: 'hispanic',
    promptValue: 'hispanic',
    // Add more details for better results with Flux
    personSuffux: 'with honey-toned skin, colombian or mesoamerican ancestry',
    label: 'Hispanic',
    imgPath: 'f/eh-cr-f-ethn-hispanic.webp',
    mImgPath: 'm/eh-cr-m-ethn-hispanic.webp'
  },
  { value: 'east_asian', label: 'East Asian', imgPath: 'f/eh-cr-f-ethn-asian.webp', mImgPath: 'm/eh-cr-m-ethn-asian.webp' },
  { value: 'indian', promptValue: 'kerala or punjabi indian', label: 'Indian', imgPath: 'f/eh-cr-f-ethn-indian.webp', mImgPath: 'm/eh-cr-m-ethn-indian.webp' },
  {
    value: 'middle_eastern',
    promptValue: 'arab',
    personSuffux: 'with amber skin, middle eastern features',
    label: 'Middle Eastern',
    imgPath: 'f/eh-cr-f-ethn-middle-eastern.webp',
    mImgPath: 'm/eh-cr-m-ethn-middle-eastern.webp'
  },
  { value: 'black', label: 'Black', imgPath: 'f/eh-cr-f-ethn-black.webp', mImgPath: 'm/eh-cr-m-ethn-black.webp' },
  {
    value: 'mixed',
    promptValue: 'mixed race',
    personSuffux: 'with blend of several ethnicities',
    label: 'Mixed',
    imgPath: 'f/eh-cr-f-ethn-mixed.webp',
    mImgPath: 'm/eh-cr-m-ethn-mixed.webp'
  },
  { value: 'random', label: 'Random', imgPath: 'f/eh-cr-f-ethn-hispanic.webp', mImgPath: 'm/eh-cr-m-ethn-caucasian.webp' },
];

const OUTFIT_OPTIONS = [
  { value: 'bikini', label: 'Bikini', fOnly: true },
  { value: 'swimsuit', label: 'Swimsuit', mOnly: true },
  { value: 'casual', promptValue: 'casual outfit', label: 'Casual' },
  { value: 'summer dress', label: 'Sun Dress', fOnly: true },
  { value: 'elegant dress', label: 'Elegant Dress', fOnly: true },
  { value: 'lingerie', label: 'Lingerie', fOnly: true },
  { value: 'crop top', label: 'Crop Top', fOnly: true },
  { value: 'leather outfit', label: 'Leather' },
  { value: 'skirt', label: 'Skirt', fOnly: true },
  { value: 'yoga outfit', label: 'Yoga', fOnly: true },
  { value: 'pyjamas', label: 'Pyjamas' },
  { value: 'suit', label: 'Suit', mOnly: true },
  { value: 'maid', label: 'Maid', fOnly: true },
  { value: 'cheerleader', label: 'Cheerleader', fOnly: true },
  { value: 'waitress', label: 'Waitress', fOnly: true },
  { value: 'nurse', label: 'Nurse', fOnly: true },
  { value: 'police', label: 'Police' },
];

const BODY_TYPE_OPTIONS = [
  { value: 'slim', label: 'Slim', imgPath: 'f/eh-cr-f-body-slim.webp', mImgPath: 'm/eh-cr-m-body-slim.webp', fOnly: true },
  { value: 'athletic', label: 'Athletic', imgPath: 'f/eh-cr-f-body-athletic.webp', mImgPath: 'm/eh-cr-m-body-athletic.webp' },
  { value: 'normal', label: 'Normal', imgPath: 'f/eh-cr-f-body-normal.webp', mImgPath: 'm/eh-cr-m-body-normal.webp' },
  { value: 'voluptuous', label: 'Voluptuous', imgPath: 'f/eh-cr-f-body-voluptuous.webp', fOnly: true },
  { value: 'muscular', label: 'Muscular', mImgPath: 'm/eh-cr-m-body-muscular.webp', mOnly: true },
  { value: 'curvy', label: 'Curvy', imgPath: 'f/eh-cr-f-body-curvy.webp', fOnly: true },
  { value: 'plump', label: 'Plump', mImgPath: 'm/eh-cr-m-body-plump.webp', mOnly: true },
];

const BUST_SIZE_OPTIONS = [
  { value: 'small', label: 'Small', imgPath: 'f/eh-cr-f-bust-small.webp' },
  { value: 'medium', label: 'Medium', imgPath: 'f/eh-cr-f-bust-medium.webp' },
  { value: 'big', label: 'Big', imgPath: 'f/eh-cr-f-bust-big.webp' },
  { value: 'huge', label: 'Huge', imgPath: 'f/eh-cr-f-bust-huge.webp' },
].map(opt => ({ ...opt, fOnly: true }));

const BUTT_SIZE_OPTIONS = [
  { value: 'small', label: 'Small', imgPath: 'f/eh-cr-f-butt-small.webp' },
  { value: 'medium', label: 'Medium', imgPath: 'f/eh-cr-f-butt-medium.webp' },
  { value: 'large', label: 'Large', imgPath: 'f/eh-cr-f-butt-large.webp' },
  { value: 'huge', label: 'Huge', imgPath: 'f/eh-cr-f-butt-huge.webp' },
].map(opt => ({ ...opt, fOnly: true }));

const ACTION_OPTIONS = [
  { value: 'selfie', label: 'Selfie' },
  { value: 'working out', label: 'Working Out' },
  { value: 'smiling', label: 'Smiling' },
  { value: 'laughing', label: 'Laughing' },
  { value: 'dining', label: 'Dining' },
  { value: 'looking_angry', label: 'Looking Angry' },
  { value: 'looking_surprised', label: 'Surprised' },
  // { value: 'tanning', label: 'Tanning' },
  { value: 'walking', label: 'Walking' },
];

const POSE_OPTIONS = [
  { value: 'standing', label: 'Standing' },
  { value: 'sitting', label: 'Sitting' },
  { value: 'squatting', label: 'Squatting' },
  { value: 'spreading legs', label: 'Spreading Legs' },
  { value: 'kneeling', label: 'Kneeling' },
  { value: 'lying', label: 'Lying' },
];

const ACCESSORIES_OPTIONS = [
  { value: 'necklace', label: 'Necklace' },
  { value: 'earrings', label: 'Earrings', fOnly: true },
  { value: 'glasses', label: 'Glasses' },
  { value: 'sunglasses', label: 'Sunglasses' },
  { value: 'hat', label: 'Hat' },
  { value: 'makeup', label: 'Makeup', fOnly: true },
];

const SCENE_OPTIONS = [
  { value: 'beach', label: 'Beach' },
  { value: 'street', label: 'Street' },
  { value: 'pool', promptValue: 'poolside', label: 'Pool' },
  { value: 'bedroom', label: 'Bedroom' },
  { value: 'park', label: 'Park' },
  { value: 'bar', label: 'Bar' },
  { value: 'gym', label: 'Gym' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'bathtub', label: 'Bathtub' },
];

const NUDE_OPTIONS = [
  { value: 'boobs', label: 'Boobs', fOnly: true },
  { value: 'butt', label: 'Butt' },
  { value: 'pubic_hair', label: 'Pubic Hair' },
  { value: 'bare_legs', label: 'Bare Legs', fOnly: true },
  // { value: 'hands_covering_breasts', label: 'Handbra', fOnly: true },
];

const POSITION_OPTIONS = [
  { value: 'missionary', promptValue: 'missionary, woman lying on back, vaginal penetration, pov, looking at viewer', label: 'Missionary', fOnly: true },
  { value: 'cowgirl', promptValue: 'cowgirl, woman on top, vaginal penetration, pov, looking at viewer', label: 'Cowgirl', fOnly: true },
  { value: 'blowjob', promptValue: 'blowjob, oral sex, erect penis', label: 'BJ' },
  { value: 'facial', promptValue: 'cum, cum trail, facial, after fellatio, male pubic hair, solo focus, cum on face, cum in mouth, cum on tongue, tongue out, open mouth', label: 'Facial', fOnly: true },
  { value: 'cumshot', promptValue: 'cumshot, cum trail, penis, a man, erection', label: 'Cumshot' },
  { value: 'doggy', promptValue: 'doggy style, on all fours, penetration from behind, man penis', label: 'Doggystyle', fOnly: true },
  { value: 'solo', promptValue: 'solo, masturbating', label: 'Solo' },
  { value: 'bdsm', promptValue: 'bdsm, bondage', label: 'BDSM' },
];

interface ImageGenState {
  style: string;
  gender: string;
  spiciness: string;
  ethnicity: string;
  outfit: string;
  bodyType: string;
  bustSize: string;
  buttSize: string;
  action: string;
  pose: string; 
  accessories: string[];
  scene: string;
  nude: string[];
  position: string;
}

type OptionsType = {
  value: string;
  label: string;
  promptValue?: string;
  imgPath?: string;
  mImgPath?: string;
  fOnly?: boolean;
  mOnly?: boolean;
  locked?: boolean;
};

export default function StudioPage() {
  const { isLoading: isLoadingUserInfo, isSubscriber, isUnlimited, is18Confirmed, user } = useUserInfo();
  const { isMobile, open: isSidebarOpen } = useSidebar();
  const { balance, refreshBalance } = useGemBalance();
  const { trackEvent } = useTracking();
  const { handleRequestVideo, COST_PER_VIDEO } = useVideo();
  
  const [state, setState] = useState<ImageGenState>({
    style: 'realistic',
    gender: 'f',
    spiciness: 'casual',
    ethnicity: '',
    outfit: '',
    bodyType: '',
    bustSize: '',
    buttSize: '',
    action: 'smiling',
    pose: '',
    accessories: [],
    scene: '',
    nude: [],
    position: 'missionary',
  });
  
  const [customPrompt, setCustomPrompt] = useState('');
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [imageStatus, setImageStatus] = useState<'idle'|'pending'|'success'|'failed'>('idle');
  const [error, setError] = useState('');
  const [currentResourceId, setCurrentResourceId] = useState<string | null>(null);
  const [upgradeLimitType, setUpgradeLimitType] = useState<'nsfw'|'image'|'video'>('image');
  const [gemCostInfo, setGemCostInfo] = useState<{
    cost: number;
    freeRemaining: number;
    freeVideoRemaining: number;
    hasEnoughGems: boolean;
  } | null>(null);
  const [isLoadingGemCostInfo, setIsLoadingGemCostInfo] = useState(true);
  const [showGemBalanceMsg, setShowGemBalanceMsg] = useState(false);
  const [showGemsDialog, setShowGemsDialog] = useState(false);
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [flaggedResource, setFlaggedResource] = useState<{id: string, url: string} | null>(null);

  // Add video generation state
  const [videoStatus, setVideoStatus] = useState<'idle'|'pending'|'success'|'failed'>('idle');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const authDialogOnCloseHandlerRef = useRef<((open: boolean, { user }: { user?: User }) => void)>((_open, _data) => {});
  const imageRetryCountRef = useRef(0);
  const imageRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imageCardRef = useRef<HTMLDivElement>(null);

  // Build prompt based on selections
  useEffect(() => {
    const parts: string[] = [];
    const isFlux = state.spiciness === 'casual';
    
    // Add basic attributes
    if (state.style === 'anime' && isFlux) parts.push('anime-style portrait'); // flux has anime support built-in
    let person = '';
    if (state.gender === 'f') person = 'woman';
    else person = 'man';
    if (state.ethnicity) {
      const ethnicity = ETHNICITY_OPTIONS.find(opt => opt.value === state.ethnicity);
      person = `${ethnicity?.promptValue || state.ethnicity} ${person}${ethnicity?.personSuffux ? ` ${ethnicity.personSuffux}` : ''}`;
    }
    person = `${state.gender === 'f' ? 'beautiful' : 'handsome'} ${person}`;
    parts.push(person);
    
    // Body type and sizes
    if (state.bodyType) parts.push(`${state.bodyType} body`);
    if (state.gender === 'f' && state.bustSize) parts.push(`${state.bustSize} breasts`);
    if (state.gender === 'f' && state.buttSize) parts.push(`${state.buttSize} butt`);
    
    // Action and pose
    if (state.action) parts.push(state.action);

    if (state.spiciness !== 'explicit' && state.pose) parts.push(state.pose);
    
    // Outfit (only if not explicit)
    if (state.spiciness !== 'explicit' && state.outfit) {
      const outfit = OUTFIT_OPTIONS.find(opt => opt.value === state.outfit);
      parts.push(`wearing ${outfit?.promptValue || state.outfit}`);
    } else if (state.spiciness === 'explicit') {
      parts.push('naked');
      if (state.gender === 'm') parts.push('penis');
    }
    
    // Accessories
    if (state.accessories.length > 0) {
      parts.push(`with ${state.accessories.join(', ')}`);
    }
    
    // Scene
    if (state.scene) {
      const scene = SCENE_OPTIONS.find(opt => opt.value === state.scene);
      parts.push(`at ${scene?.promptValue || state.scene}`);
    }
    
    // Nude options
    if (state.spiciness === 'nude' && state.nude.length > 0) {
      parts.push(state.nude.map(nude => `showing ${nude}`).join(', '));
    }
    
    // Position options (for explicit)
    if (state.spiciness === 'explicit' && state.position) {
      const position = POSITION_OPTIONS.find(opt => opt.value === state.position);
      parts.push(position?.promptValue || state.position);
    }
    
    setCustomPrompt(prev => {
      // Let user add persistent text after last comma
      const userPart = prev.split(',').slice(-1).pop()?.trim();

      const newPrompt = parts.map(p => p.replace(/_/g, ' ').trim()).join(', ');
      return `${newPrompt}, ${userPart || ''}`;
    });
  }, [state]);

  // Calculate gem cost information
  const calculateGemCost = useCallback(async () => {
    if (!user) {
      setGemCostInfo(null);
      setIsLoadingGemCostInfo(false);
      return;
    }
    setIsLoadingGemCostInfo(true);

    try {
      //  free trial only for non-subscribers
      let recentImages = (isSubscriber && !isUnlimited) ? FREE_IMAGE_DAILY_LIMIT + 1 : 0;
      let recentVideos = isSubscriber ? FREE_VIDEO_MONTHLY_LIMIT + 1 : 0;

      if (!isSubscriber) {
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const [imageResult, videoResult] = await Promise.all([
          supabaseEh
            .from('media_requests')
            .select('id')
            .eq('user_id', user.id)
            .eq('media_type', 'image')
            .eq('status', 'success')
            .gte('created_at', twentyFourHoursAgo),
          supabaseEh
            .from('media_requests')
            .select('id, created_at')
            .eq('user_id', user.id)
            .eq('media_type', 'video')
            .eq('status', 'success')
            .gte('created_at', oneMonthAgo)
            .order('created_at', { ascending: false })
            .limit(FREE_VIDEO_MONTHLY_LIMIT + 1)
        ]);

        const { data: images, error: imageError } = imageResult;
        const { data: videos, error: videoError } = videoResult;

        if (imageError || videoError) {
          trackEvent(UI_EVENTS.UI_ERROR, {
            source: 'StudioPage.calculateGemCost',
            message: 'Error fetching recent images',
            error: imageError?.message || videoError?.message
          });
          setGemCostInfo(null);
          return;
        }
        recentImages = images?.length || 0;
        recentVideos = videos?.length || 0;
      }

      const isWithinFreeLimit = recentImages < FREE_IMAGE_DAILY_LIMIT;
      const cost = isWithinFreeLimit ? 0 : COST_PER_IMAGE;
      const freeRemaining = Math.max(0, FREE_IMAGE_DAILY_LIMIT - recentImages);
      const freeVideoRemaining = Math.max(0, FREE_VIDEO_MONTHLY_LIMIT - recentVideos);
      const hasEnoughGems = cost === 0 || balance.total >= cost;

      setGemCostInfo({
        cost,
        freeRemaining,
        freeVideoRemaining,
        hasEnoughGems,
      });
    } catch (err) {
      trackEvent(UI_EVENTS.UI_ERROR, {
        source: 'StudioPage.calculateGemCost',
        message: 'Error calculating gem cost',
        error: err instanceof Error ? err.message : 'Unknown error'
      });
      setGemCostInfo(null);
    } finally {
      setIsLoadingGemCostInfo(false);
    }
  }, [balance.total, isSubscriber, isUnlimited, trackEvent, user]);

  useEffect(() => {
    calculateGemCost();
  }, [calculateGemCost]);

  const genderFilter = (opt: OptionsType) => {
    return (opt.fOnly && state.gender === 'f')
      || (opt.mOnly && state.gender === 'm')
      || (!opt.fOnly && !opt.mOnly);
  };

  const handleSelect = (key: keyof ImageGenState, value: string | string[]) => {
    const canReset = !['spiciness', 'style', 'gender'].includes(key);
    const isRandomEthnicity = key === 'ethnicity' && value === 'random';
    if (isRandomEthnicity) {
      const choices = ETHNICITY_OPTIONS.filter(o => o.value !== 'random');
      value = choices[Math.floor(Math.random() * choices.length)].value;
    }
    setState(prev => ({ ...prev, [key]: (canReset && prev[key] === value && !isRandomEthnicity) ? '' : value }));
  };

  const handleMultiSelect = (key: keyof ImageGenState, value: string) => {
    setState(prev => {
      const currentArray = prev[key] as string[];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return { ...prev, [key]: newArray };
    });
  };

  const handleMediaRequest = async (customPrompt: string, resourceId: string, { requestId }: {
    requestId?: string,
  } = {}) => {
    setImageStatus('pending');
    setError('');

    // remove all text of nude/explict options. Very basic sanitization.
    // For forbidden content, rely on image API to filter it out.
    const explicitParts = POSITION_OPTIONS.reduce((acc, opt) => {
        acc.push(...(opt.promptValue || opt.value).split(',').map(part => part.trim()));
      return acc;
    }, ['nude', 'naked'] as string[]);
    
    let sanitizedPrompt = customPrompt
      .trim()
      .replace(/,$/, '')
    
    if (!isSubscriber) {
      sanitizedPrompt = sanitizedPrompt
        .replace(new RegExp(`(${explicitParts.join('|')})`, 'g'), '')
        .split(',')
        .filter(part => part.trim().length > 0)
        .join(',');
    }

    if (import.meta.env.DEV) console.log('[DEV] sanitizedPrompt:', sanitizedPrompt);

    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session?.access_token) {
        throw new Error('No authentication token available');
      }

      const mediaRequestResponse = await fetch(`${SUPABASE_URL}/functions/v1/eh-media-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.data.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customPrompt: sanitizedPrompt,
          style: `${state.spiciness !== 'casual' ? 'h' : ''}${state.style}`,
          requestId,
          resource_id: resourceId,
          media_type: 'image',
        }),
      });

      if (!mediaRequestResponse.ok && mediaRequestResponse.status === 402) {
        setImageStatus('failed');
        setError(`Please ${isSubscriber ? 'buy more gems' : 'subscribe'} to generate more images.`);
        await refreshBalance();
        setShowGemsDialog(true);
        return { success: false };
      } else if (!mediaRequestResponse.ok && imageRetryCountRef.current > 0) { // retry only once after error
        setImageStatus('failed');
        setError('Oops, something went wrong. Please try again.');
        return { success: false };
      }

      const mediaData = await mediaRequestResponse.json();
      
      if (!mediaRequestResponse.ok || (mediaData?.retryAfter && imageRetryCountRef.current < 3)) {
        imageRetryCountRef.current++;
        // retryAfter is an epoch timestamp, compute delay in ms
        const delay = mediaData?.retryAfter ? mediaData.retryAfter - Date.now() : 5000;
        imageRetryTimeoutRef.current = setTimeout(async () => {
          await handleMediaRequest(customPrompt, resourceId, {
            requestId: mediaData?.requestId || undefined,
          });
        }, Math.max(delay, 0));
        return { success: false, retrying: true };
      }

      if (import.meta.env.DEV) console.log('[DEV] handleMediaRequest data:', mediaData);

      if (!mediaData?.success || !mediaData?.filePath) {
        setImageStatus('failed');
        setError('Oops, something went wrong. Please retry later.');
        return { success: false };
      }

      await refreshBalance();
      // balance does not change if not a subscriber, so we need to recalculate gem cost manually
      if (!isSubscriber) {
        await calculateGemCost();
      }

      setGeneratedImageUrl(getMediaUrl(mediaData.filePath));
      setImageStatus('success');
      imageRetryCountRef.current = 0; // Reset retry count on success
      return { success: true, data: mediaData };
    } catch (err) {
      setImageStatus('failed');
      setError(import.meta.env.DEV && err instanceof Error ? `[DEV] ${err.message}` : 'Oops, something went wrong. Please try again.');
      return { success: false };
    }
  };

  const checkImageGeneration24hLimit = async (): Promise<boolean> => {
    if (!user || isSubscriber) return false;
    
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    
    const { data, error } = await supabaseEh
      .from('media_requests')
      .select('id')
      .eq('user_id', user.id)
      .eq('media_type', 'image')
      .eq('status', 'success')
      .gte('created_at', twentyFourHoursAgo);
    
    if (error) {
      console.error('Error checking image generation limit:', error);
      return false;
    }
    
    return (data?.length || 0) >= FREE_IMAGE_DAILY_LIMIT && balance.total < COST_PER_IMAGE;
  };

  const handleGenerate = async () => {
    if (!user) {
      authDialogOnCloseHandlerRef.current = (open, { user }) => {
        setShowAuthDialog(false);
        if (!open && user) handleGenerate();
      };
      setShowAuthDialog(true);
      return;
    }

    if (gemCostInfo?.hasEnoughGems === false) {
      if (showGemBalanceMsg) { // already clicked
        window?.open('/gems', '_blank');
        trackEvent(PAYMENT_EVENTS.GEM_BALANCE_MSG_CLICKED, {
          source: 'ImageGenPage.handleGenerate',
          message: 'Get more gems',
          amount: gemCostInfo?.cost,
        });
        return;
      }
      setShowGemsDialog(true);
      setShowGemBalanceMsg(true);
      return;
    }
    setShowGemBalanceMsg(false);

    // Check subscription limits for nude/explicit content
    if (!isSubscriber && (state.spiciness === 'nude' || state.spiciness === 'explicit')) {
      setUpgradeLimitType('nsfw');
      setShowUpgradeDialog(true);
      return;
    }

    // Check 24h limit for non-subscribers
    if (await checkImageGeneration24hLimit()) {
      setShowGemsDialog(true);
      // setUpgradeLimitType('image');
      // setShowUpgradeDialog(true);
      return;
    }

    if (isGenerating) return;

    setIsGenerating(true);
    setError('');
    setGeneratedImageUrl(null);
    setVideoUrl(null);
    setVideoStatus('idle');
    imageRetryCountRef.current = 0;

    // Scroll to image card on mobile when starting generation
    if (!isLargeScreen && imageCardRef.current) {
      setTimeout(() => {
        imageCardRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }

    try {
      const resourceId = generateBase58Id('img');
      setCurrentResourceId(resourceId);
      
      const result = await handleMediaRequest(customPrompt, resourceId);
      if (result.success && result.data?.mrId) {
        // Replace generated img id with server-side media request id in case of flagging
        setCurrentResourceId(result.data.mrId);
      }
    } catch (err) {
      setError(import.meta.env.DEV && err instanceof Error ? `[DEV] ${err.message}` : 'Oops, something went wrong. Please try again.');
      setImageStatus('failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetryGeneration = async () => {
    if (!currentResourceId) return;
    
    setIsGenerating(true);
    setError('');
    imageRetryCountRef.current = 0;
    
    try {
      await handleMediaRequest(customPrompt, currentResourceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate image');
      setImageStatus('failed');
    } finally {
      setIsGenerating(false);
    }
  };

  // Add video generation function
  const handleStudioVideoRequest = async (imageUrl: string, customPrompt: string, resourceId: string) => {
    setVideoStatus('pending');
    
    const result = await handleRequestVideo({
      imageUrl,
      customPrompt,
      resourceId,
      mediaType: 'video',
      mrOptions: {
        // avoid explicit content in video unless image mode is explicit
        safeMode: !is18Confirmed || state.spiciness !== 'explicit',
        style: `${state.spiciness !== 'casual' ? 'h' : ''}${state.style}`,
      }
    });

    if (result.success && typeof result.data?.filePath === 'string') {
      setVideoStatus('success');
      setVideoUrl(getMediaUrl(result.data.filePath as string));
    } else if (result.data?.error === 'upgrade_required') {
      setShowGemsDialog(true);
      // setUpgradeLimitType('video');
      // setShowUpgradeDialog(true);
      setVideoStatus('idle');
    } else if (result.data?.error === 'low_gem_balance') {
      setShowGemsDialog(true);
      setVideoStatus('idle');
    } else {
      setVideoStatus('failed');
    }
  };

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (imageRetryTimeoutRef.current) {
        clearTimeout(imageRetryTimeoutRef.current);
      }
    };
  }, []);

  const renderOptionsGrid = (options: OptionsType[], selectedKey: keyof ImageGenState, isMultiSelect = false) => {
    const selected = isMultiSelect ? state[selectedKey] as string[] : [state[selectedKey] as string];
    
    return (
      <div className="flex flex-wrap gap-2 items-center justify-center select-none">
        {options.filter(genderFilter).map(opt => {
          const isSelected = isMultiSelect ? selected.includes(opt.value) : selected[0] === opt.value;
          const locked = opt.locked && !isSubscriber;
          const hasImg = opt.imgPath || (state.gender === 'm' && opt.mImgPath);
          const someHasImg = options.some(o => o.imgPath || (state.gender === 'm' && o.mImgPath));
          
          return (
            <button
              key={opt.value}
              className={`relative rounded-sm text-sm overflow-hidden border-2 transition-all flex flex-col items-center justify-end ${
                someHasImg ? 'w-28 h-32' : 'h-10'
              } ${
                someHasImg && !hasImg ? 'bg-zinc-700' : ''
              } ${
                isSelected ? 'border-pink-500' : 'border-zinc-700'
              } ${
                locked ? 'opacity-50' : 'hover:border-pink-400'
              }
              `}
              onClick={() => {
                if (locked) {
                  setUpgradeLimitType('image');
                  setShowUpgradeDialog(true);
                  return;
                }
                if (isMultiSelect) {
                  handleMultiSelect(selectedKey, opt.value);
                } else {
                  handleSelect(selectedKey, opt.value);
                }
              }}
              type="button"
            >
              {someHasImg ? (
                <>
                  {hasImg && <img 
                    src={`/images/demo/${state.gender === 'm' ? opt.mImgPath : opt.imgPath}`} 
                    alt={opt.label} 
                    className={`w-full h-full object-cover ${locked ? 'blur-sm' : ''} ${opt.value === 'random' ? 'blur-sm' : ''}`}
                  />}
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white text-sm font-medium">
                    {opt.label}
                  </span>
                </>
              ) : (
                <div className={`w-full h-full bg-zinc-700 flex items-center justify-center ${locked ? 'blur-sm' : ''}`}>
                  <span className="text-white font-medium text-center px-2">{opt.label}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const renderSpicinessButtons = () => (
    <div className="flex flex-wrap gap-2 select-none">
      {SPICINESS_OPTIONS
      // explicit is only available for 18+ users (or shown to visitors as disabled options)
      .filter(opt => opt.value !== 'explicit' || !user || is18Confirmed)
      // explicit is only available in apps for 18+ users
      .filter(opt => ['casual', 'nude'].includes(opt.value) || !isFromAppStores() || is18Confirmed)
      .map(opt => {
        const locked = opt.locked && !isSubscriber;
        const isSelected = state.spiciness === opt.value;
        
        return (
          <button
            key={opt.value}
            className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm font-medium text-sm sm:text-base transition-all ${
              isSelected ? 'bg-amber-600 text-white' : locked ? 'bg-zinc-600 text-zinc-400' : 'bg-zinc-700 text-white hover:bg-zinc-600'
            } ${
              locked ? `opacity-50${isFromAppStores() ? ' blur-md' : ''}` : ''
            }`}
            onClick={() => {
              handleSelect('spiciness', opt.value);
            }}
            type="button"
          >
            {isFromAppStores() && opt.storeLabel ? opt.storeLabel : opt.label}
          </button>
        );
      })}
    </div>
  );

  const canGenerate = customPrompt.trim().length > 0;
  const isLoadingCost = isLoadingUserInfo || isLoadingGemCostInfo;

  const [isLargeScreen, setIsLargeScreen] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const showImageCard = isGenerating || imageStatus !== 'idle' || isLargeScreen;

  return (
    <div className="flex-auto flex flex-col bg-zinc-900">
      <SEO 
        title="Image & Video Studio - EveningHoney.AI"
        description="Create your own images and videos with AI and make your dreams come true. Customize every detail and create the perfect image: casual, suggestive, or explicit (NSFW)."
        ogUrl="/studio"
      />
      <div className="flex-1 lg:container max-w-6xl mx-auto px-4 py-8 max-lg:mb-16">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">AI Studio</h1>
          <p className="text-zinc-300">Make your dreams come true in your own images & videos</p>
        </div>

        {/* Main content container with gradient border */}
        <div className="rounded-lg px-4 md:px-8 pt-4 md:pt-8 pb-16 md:pb-24 bg-gradient-to-br from-zinc-800/70 to-zinc-950/70 border border-zinc-800/80 shadow-lg">
          <div className="grid xl:grid-cols-12 gap-6">
            {/* Left Column - Controls */}
            <div className="xl:col-span-7 space-y-6">
              {/* Style & Gender */}
              <div className="bg-zinc-900/50 rounded-lg p-4">
                <div className="flex flex-wrap justify-between gap-4">
                  <div>
                    {/* <div className="block text-white font-medium mb-2">Style</div> */}
                    <div className="flex gap-2 select-none">
                      {STYLE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          className={`px-2 sm:px-4 py-1.5 sm:py-2  rounded-sm font-medium text-sm sm:text-base transition-all flex-1
                            ${state.style === opt.value ? 'bg-amber-600 text-white' : 'bg-zinc-700 text-white hover:bg-zinc-600'}
                          `}
                          onClick={() => handleSelect('style', opt.value)}
                          type="button"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    {/* <div className="block text-white font-medium mb-2">Gender</div> */}
                    <div className="flex gap-2 select-none">
                      {GENDER_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-sm font-medium text-sm sm:text-base transition-all flex-1 ${
                            state.gender === opt.value
                              ? `text-white ${opt.value === 'f' ? 'bg-pink-700' : 'bg-cyan-700'}`
                              : 'bg-zinc-700 text-white hover:bg-zinc-600'
                          }`}
                          onClick={() => handleSelect('gender', opt.value)}
                          type="button"
                        >
                          {opt.value === 'f'
                            ? <Venus className="size-5 sm:size-6" />
                            : <Mars className="size-5 sm:size-6" />
                          }
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Spiciness */}
              <div className="bg-zinc-900/50 rounded-lg p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="text-white font-medium">
                    {isFromAppStores() ? 'Atmosphere' : 'Spiciness'}
                  </div>
                  {renderSpicinessButtons()}
                </div>
                {!isSubscriber && (['nude', 'explicit'].includes(state.spiciness) && (
                  <div className="text-zinc-500 text-xs text-center sm:text-end p-2 select-none">
                    Explicit content available for subscribers only
                  </div>
                ))}
              </div>

              {/* Tabbed Options */}
              <div className="bg-zinc-900/50 rounded-lg p-4">
                {/* <div className="text-white font-medium mb-3 text-center">Arrange every detail</div> */}
                <Tabs defaultValue="outfit" className="w-full justify-center items-center text-center">
                  <TabsList className="2xl:px-8 bg-zinc-800 flex flex-wrap">
                    {state.spiciness !== 'explicit' && (
                      <TabsTrigger value="outfit">Outfit</TabsTrigger>
                    )}
                    <TabsTrigger value="ethnicity">Ethnicity</TabsTrigger>
                    <TabsTrigger value="body">Body</TabsTrigger>
                    <TabsTrigger value="action">Action</TabsTrigger>
                    {state.spiciness !== 'explicit' && (
                      <TabsTrigger value="pose">Pose</TabsTrigger>
                    )}
                    <TabsTrigger value="accessories">Accessories</TabsTrigger>
                    <TabsTrigger value="scene">Scene</TabsTrigger>
                    {!isFromAppStores() && state.spiciness === 'nude' && (
                      <TabsTrigger value="nude">🍑 Nude</TabsTrigger>
                    )}
                    {state.spiciness === 'explicit' && (
                      <TabsTrigger value="position">🌶️ Position</TabsTrigger>
                    )}
                  </TabsList>
                  
                  {state.spiciness !== 'explicit' && (
                    <TabsContent value="outfit" className="mt-4">
                      <h3 className="text-white font-medium mb-3">Outfit</h3>
                      {renderOptionsGrid(isFromAppStores() && !is18Confirmed ? OUTFIT_OPTIONS
                          : [...OUTFIT_OPTIONS, { value: 'no clothes, naked', label: 'Nude' }],
                        'outfit'
                      )}
                    </TabsContent>
                  )}
                  
                  <TabsContent value="ethnicity" className="mt-4">
                    <h3 className="text-white font-medium mb-3">Ethnicity</h3>
                    {renderOptionsGrid(ETHNICITY_OPTIONS, 'ethnicity')}
                  </TabsContent>
                  
                  <TabsContent value="body" className="mt-4">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-white font-medium mb-3">Body Type</h3>
                        {renderOptionsGrid(BODY_TYPE_OPTIONS, 'bodyType')}
                      </div>
                      {(!isFromAppStores() || is18Confirmed) && state.spiciness !== 'casual' && state.gender === 'f' && (
                        <>
                          <div>
                            <h3 className="text-white font-medium mb-3">Bust Size</h3>
                            {renderOptionsGrid(BUST_SIZE_OPTIONS, 'bustSize')}
                          </div>
                          <div>
                            <h3 className="text-white font-medium mb-3">Butt Size</h3>
                            {renderOptionsGrid(BUTT_SIZE_OPTIONS, 'buttSize')}
                          </div>
                        </>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="action" className="mt-4">
                    <h3 className="text-white font-medium mb-3">Action</h3>
                    {renderOptionsGrid(ACTION_OPTIONS, 'action')}
                  </TabsContent>
                  
                  {state.spiciness !== 'explicit' && (
                    <TabsContent value="pose" className="mt-4">
                      <h3 className="text-white font-medium mb-3">Pose</h3>
                      {renderOptionsGrid(POSE_OPTIONS, 'pose')}
                    </TabsContent>
                  )}
                  <TabsContent value="accessories" className="mt-4">
                    <h3 className="text-white font-medium mb-3">Accessories, multiple allowed</h3>
                    {renderOptionsGrid(ACCESSORIES_OPTIONS, 'accessories', true)}
                  </TabsContent>
                  
                  <TabsContent value="scene" className="mt-4">
                    <h3 className="text-white font-medium mb-3">Scene</h3>
                    {renderOptionsGrid(SCENE_OPTIONS, 'scene')}
                  </TabsContent>
                  
                  {!isFromAppStores() && state.spiciness === 'nude' && (
                    <TabsContent value="nude" className="mt-4">
                      <h3 className="text-white font-medium mb-3">Nude Options, multiple allowed</h3>
                      {renderOptionsGrid(NUDE_OPTIONS, 'nude', true)}
                    </TabsContent>
                  )}
                  
                  {state.spiciness === 'explicit' && (
                    <TabsContent value="position" className="mt-4">
                      <h3 className="text-white font-medium mb-3">Position</h3>
                      {renderOptionsGrid(shuffleArray([...POSITION_OPTIONS]), 'position')}
                    </TabsContent>
                  )}
                </Tabs>
              </div>

              {/* Prompt Preview */}
              <div className="bg-zinc-900/50 rounded-lg p-4">
                <Label htmlFor="custom-prompt" className="block text-white font-medium text-md mb-3">
                  <span>Customize the prompt </span>
                  <span className="block text-zinc-500 font-normal text-xs">
                    Add your own text after the last comma to keep it when selecting presets above
                  </span>
                </Label>
                <textarea
                  id="custom-prompt"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  className="w-full h-32 p-3 rounded-sm bg-zinc-700 text-white resize-none"
                  placeholder="Your prompt will appear here..."
                />
              </div>
            </div>

            {/* Right Column - Generated Image (only show on desktop or when generating) */}

            <div ref={imageCardRef} className={`xl:col-span-5 space-y-6 ${showImageCard ? 'block' : 'hidden'}`}>
              <div className="bg-zinc-900/50 rounded-lg p-4">
                <h3 className="text-white font-medium mb-4">Generated Image</h3>
                <div className="aspect-[13/19] sm:max-h-[90dvh] mx-auto bg-zinc-700 rounded-sm flex items-center justify-center overflow-hidden relative">
                  {isGenerating ? (
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 border-4 border-zinc-600 border-t-pink-500 rounded-full animate-spin mb-4"></div>
                      <p className="text-zinc-300">Generating your image...</p>
                    </div>
                  ) : generatedImageUrl ? (
                    <>
                      {videoUrl && videoStatus === 'success' ? (
                        <video
                          src={videoUrl}
                          controls
                          autoPlay
                          loop
                          muted
                          className="max-w-full object-contain aspect-[13/19]"
                        />
                      ) : (
                        <img
                          src={generatedImageUrl} 
                          alt="Generated image" 
                          className="w-full h-full object-cover"
                        />
                      )}
                      {/* Video Generation Button Overlay */}
                      <div className="absolute bottom-4 right-4">
                        <Button
                          variant="outline"
                          className={`h-auto py-1 border-none bg-black/50 hover:bg-pink-600/60 text-white hover:text-white ${
                            videoStatus === 'pending' ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => handleStudioVideoRequest(generatedImageUrl, customPrompt, currentResourceId || '')}
                          disabled={videoStatus === 'pending'}
                        >
                          {videoStatus === 'pending' ? (
                            <span className="w-4 h-4 border-2 border-zinc-600 border-t-pink-500 rounded-full animate-spin"></span>
                          ) : (
                            <>
                              <Video className="size-5" />
                              <span>{videoUrl ? 'Regenerate' : 'Generate Video'}</span>
                              {!gemCostInfo?.freeVideoRemaining /* && !isUnlimited  */? (
                                // Studio videos are not included in unlimited package (chat only) to prevent abuse
                                <span className="text-xs bg-white/20 rounded-sm p-1">{`${COST_PER_VIDEO} 💎`}</span>
                              ) : (
                                <span className="text-xs bg-white/20 rounded-sm px-2 py-1">Free Trial</span>
                              )}
                            </>
                          )}
                        </Button>
                      </div>
                      {/* Video Generation Loading/Error Overlay */}
                      {videoStatus === 'pending' && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="bg-zinc-800/60 rounded-lg p-6 text-center">
                            <div className="w-8 h-8 border-2 border-zinc-600 border-t-pink-500 rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-white text-sm">Generating video...</p>
                          </div>
                        </div>
                      )}
                      {videoStatus === 'failed' && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="bg-zinc-800/80 rounded-lg p-6 text-center">
                            <p className="text-red-400 mb-3">Video generation failed</p>
                            <button
                              onClick={() => handleStudioVideoRequest(generatedImageUrl, customPrompt, currentResourceId || '')}
                              className="px-4 py-2 bg-pink-600 text-white rounded-sm hover:bg-pink-500 transition-colors text-sm"
                              type="button"
                            >
                              Retry
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : imageStatus === 'failed' ? (
                    <div className="flex flex-col items-center text-center">
                      <p className="text-red-400 mb-4">Generation failed</p>
                      <button
                        onClick={handleRetryGeneration}
                        className="px-4 py-2 bg-pink-600 text-white rounded-sm hover:bg-pink-500 transition-colors"
                        type="button"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <p className="text-zinc-400 text-center select-none">
                      <span>Your generated image will appear here.</span>
                      <br />
                      <span>Then, generate a video if you like what you see!</span>
                    </p>
                  )}
                </div>
                {generatedImageUrl && (
                  <div className="flex justify-center mt-4 items-center gap-4">
                    <p className="mx-auto text-zinc-300 text-sm italic text-center select-none">
                      <Link to="/gallery" className="font-semibold text-amber-500 hover:text-amber-400">NEW: private gallery </Link>
                      <span>of all photos & videos generated. </span>
                    </p>
                    <button
                      onClick={() => {
                        if (currentResourceId && generatedImageUrl) {
                          setFlaggedResource({ id: currentResourceId, url: generatedImageUrl });
                          setShowFlagDialog(true);
                        }
                      }}
                      className="flex-[0_0_auto] -mt-4 flex items-center justify-center size-8 rounded-full bg-zinc-800 hover:bg-zinc-700"
                    >
                      <Flag className="size-4 text-zinc-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Generate Button on Mobile */}
      <div
        className="fixed bottom-0 right-0 bg-zinc-900 max-lg:border-t border-zinc-800 p-4 lg:block lg:container lg:mx-auto lg:max-w-6xl lg:px-4 transition-all"
        style={{
          left: isMobile ? 0 : (isSidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON),
          maxWidth: isMobile ? '' : (isSidebarOpen ? `calc(100vw - ${SIDEBAR_WIDTH})` : `calc(100vw - ${SIDEBAR_WIDTH_ICON})`),
        }}
      >
        <div className="space-y-3">
          {error && (
            <div className="text-red-500 text-center text-sm lg:text-base">{error}</div>
          )}
          
          {/* Gem cost information for mobile */}
          {/* {!isSubscriber && !!user && gemCostInfo && (
            gemCostInfo.cost > 0 ? (
              <div className={`text-center px-3 py-2 rounded-lg ${gemCostInfo.hasEnoughGems ? 'bg-zinc-700' : 'bg-red-900'}`}>
                <div className="text-xs text-zinc-300">
                  <span>Image generation costs </span>
                  <span className="font-semibold">{gemCostInfo.cost} 💎</span>
                  <span> (balance: {balance.total} 💎)</span>
                </div>
                {!gemCostInfo.hasEnoughGems && (
                  <div className="text-red-400 text-xs mt-1">
                    <span>Insufficient gems. </span>
                    <span className="text-blue-400 cursor-pointer underline" onClick={() => setShowUpgradeDialog(true)}>
                      Get more gems
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center px-3 py-2 rounded-lg bg-green-900">
                <div className="text-xs text-green-300">
                  <span>Free images remaining: {gemCostInfo.freeRemaining}/2 today</span>
                </div>
              </div>
            )
          )} */}
          
          <button
            className="block w-full lg:container mx-auto px-6 py-3 rounded-sm bg-pink-600 text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleGenerate}
            disabled={!canGenerate || isGenerating}
            type="button"
          >
            {isGenerating ? (
              <div className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>Generating...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="flex items-center justify-center gap-2">
                  <span>Generate Photo</span>
                  {((gemCostInfo?.cost ?? 0) > 0 && !isLoadingCost) && !isUnlimited && (
                    <span className="text-sm bg-pink-700/80 rounded-sm px-2 py-1">{`${gemCostInfo?.cost} 💎`}</span>
                  )}
                </div>
                {!isLoadingCost && !isUnlimited && (
                  <div className="text-xs">
                    {gemCostInfo?.cost === 0 ? (<span>Free trial</span>) : (!gemCostInfo?.hasEnoughGems && showGemBalanceMsg) && (
                      <span className="underline">
                        Get more gems
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}  
          </button>
          {!isSubscriber && !isLoadingUserInfo && !!user && !isHidingMonetizationInStores() && (
            <p className="text-zinc-400 text-xs lg:text-sm text-center select-none">
              <Link to="/subscription" className="inline-flex items-center justify-center gap-2 hover:underline text-amber-500 hover:text-amber-400">
                <Crown className="w-4 h-4" />
                <span>Subscribe for unlimited images</span> 
            </Link>
            </p>
          )}
        </div>
      </div>

      <EhUpgradeDialog 
        open={showUpgradeDialog} 
        onOpenChange={setShowUpgradeDialog}
        onUpgradeClick={() => {
          if (!user) {
            setShowAuthDialog(true);
            return false;
          }
          return true;
        }}
        limitType={upgradeLimitType}
      />
      
      <EhAuthDialog
        open={showAuthDialog}
        onOpenChange={authDialogOnCloseHandlerRef.current}
      />

      <EhGemsDialog
        open={showGemsDialog}
        onOpenChange={setShowGemsDialog}
        action={'image'}
      />
      {flaggedResource && (
        <EhFlagDialog
          open={showFlagDialog}
          onOpenChange={setShowFlagDialog}
          flaggedResourceId={flaggedResource.id}
          onClose={(flaggingSuccess) => {
            setFlaggedResource(null);
            if (flaggingSuccess) setGeneratedImageUrl(null);
          }}
          thumbnailUrl={flaggedResource.url}
          flagData={{
            studioState: state
          }}
        />
      )}
    </div>
  );
} 